import { useEffect } from "react";

import {
	DEFAULT_SCENARIO_ID,
	SCENARIO_REGISTRY,
	type ScenarioRegistryEntry,
	isProethicsScenarioId,
} from "../data/scenario-registry";
import { type ScenarioBundle, useScenarioBundle } from "../data/scenarios";
import type { ProethicsScenarioId } from "../types";
import { useProethicsFlowStore } from "./useProethicsFlowStore";

export type ActiveScenario = ScenarioBundle & {
	registry: ScenarioRegistryEntry;
};

export const useActiveScenario = (): ActiveScenario => {
	const scenarioId = useProethicsFlowStore((s) => s.scenarioId);
	return {
		...useScenarioBundle(scenarioId),
		registry: SCENARIO_REGISTRY[scenarioId],
	};
};

export const resolveScenarioId = (
	scenarioId: string | undefined,
): ProethicsScenarioId =>
	isProethicsScenarioId(scenarioId) ? scenarioId : DEFAULT_SCENARIO_ID;

// Read-only: true when the store's active scenario matches this one, so cards
// can gate rendering and avoid flashing another scenario's run. The active
// scenario is driven centrally by useSyncActiveScenarioFromItem - cards must not
// set it themselves, or windowed (off-screen but mounted) neighbours would
// fight over the single active pointer.
export const useIsActiveScenario = (
	scenarioId: string | undefined,
): boolean => {
	const resolved = resolveScenarioId(scenarioId);
	const activeId = useProethicsFlowStore((s) => s.scenarioId);
	return activeId === resolved;
};

// Drives the active scenario from the session's current item. The session list
// keeps neighbouring items mounted, so this is the single, deterministic place
// that switches scenarios - keyed off the current item's id, not per-card mount.
// No-op for items without a scenarioId, so non-ProEthics sessions never touch
// the store.
export const useSyncActiveScenarioFromItem = (
	scenarioId: string | undefined,
): void => {
	useEffect(() => {
		if (!isProethicsScenarioId(scenarioId)) {
			return;
		}
		useProethicsFlowStore.getState().setScenario(scenarioId);
	}, [scenarioId]);
};
